import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  MapPin,
  Users,
  X,
  Search,
  SlidersHorizontal,
  CircleDashed,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import {
  SearchAutocomplete,
  type SearchAutocompleteHandle,
} from "@/components/inputs/search-autocomplete/search-autocomplete";
import { useSearchSpaces } from "@/hooks/useSearchSpaces";
import { spaceService } from "@/services/space-service";
import { useAuth } from "@/features/authentication";
import { SpaceDetailDrawer } from "../ai-mode/components/SpaceDetailDrawer";
import { SpaceCard } from "../ai-mode/components/SpaceCard";
import { AISearchLoader } from "../ai-mode/components/AISearchLoader";
import { AISearchError } from "../ai-mode/components/AISearchError";
import {
  useSearchAndRank,
  buildSearchAndRankBody,
} from "../ai-mode/hooks/useSearchAndRank";
import type { LivePreview, RankedSpace, DiagnosisData } from "../ai-mode/types";
import { getDefaultSearchSource } from "@/services/vision-service";
import { buildPerfectMockSpace, PERFECT_MOCK_ID } from "./perfectMockSpace";
import {
  trackPartnerDemoSearchInitiated,
  trackPartnerDemoSpaceSelected,
} from "./analytics";

const RADIUS_OPTIONS = [
  { value: 3, label: "3 km" },
  { value: 5, label: "5 km" },
  { value: 30, label: "30 km" },
] as const;

interface PartnerSearchStepProps {
  onNext: (selectedSpaces: RankedSpace[]) => void;
  onPreviewUpdate: (data: Partial<LivePreview>) => void;
  onValidityChange: (valid: boolean) => void;
  registerNext: (fn: () => void) => void;
}

export default function PartnerSearchStep({
  onNext,
  onPreviewUpdate,
  onValidityChange,
  registerNext,
}: PartnerSearchStepProps) {
  const [location, setLocation] = useState<{
    label: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [locationInputValue, setLocationInputValue] = useState("");
  const [radiusKm, setRadiusKm] = useState<number>(5);
  const [customRadiusMode, setCustomRadiusMode] = useState(false);
  const [customRadiusInput, setCustomRadiusInput] = useState("");
  const [capacityMin, setCapacityMin] = useState("");
  const [capacityMax, setCapacityMax] = useState("");
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<string[]>([]);
  const [drawerSpace, setDrawerSpace] = useState<RankedSpace | null>(null);
  const [activeSpaceId, setActiveSpaceId] = useState<string | null>(null);
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);

  const { userInfo } = useAuth();
  const {
    spaces: searchSuggestions,
    handleSearchSpaces,
    isLoading: isSearchLoading,
  } = useSearchSpaces();
  const locationRef = useRef<SearchAutocompleteHandle>(null);
  const {
    spaces: rankedSpaces,
    status: rankStatus,
    searchAndRank,
  } = useSearchAndRank();

  const effectiveSearchSource = getDefaultSearchSource();

  const perfectMock = useMemo(
    () =>
      buildPerfectMockSpace({
        location,
        capacityMin: capacityMin !== "" ? Number(capacityMin) : null,
        capacityMax: capacityMax !== "" ? Number(capacityMax) : null,
      }),
    [location, capacityMin, capacityMax],
  );

  const allSpaces: RankedSpace[] = useMemo(() => {
    if (rankStatus !== "success" || rankedSpaces.length === 0) return [];
    const real = rankedSpaces.filter((s) => s.id !== PERFECT_MOCK_ID);
    return [perfectMock, ...real];
  }, [rankStatus, rankedSpaces, perfectMock]);

  const matchedSpaces = useMemo(
    () => allSpaces.filter((s) => s.id === PERFECT_MOCK_ID || s.score >= 80),
    [allSpaces],
  );
  const otherSpaces = useMemo(
    () => allSpaces.filter((s) => s.id !== PERFECT_MOCK_ID && s.score < 80),
    [allSpaces],
  );

  const [showOtherSpaces, setShowOtherSpaces] = useState(false);

  const searchStartTimeRef = useRef<number | null>(null);
  const lastBodyRef = useRef<ReturnType<typeof buildSearchAndRankBody> | null>(
    null,
  );

  const hasValidLocation =
    !!location &&
    location.lat != null &&
    location.lng != null &&
    !(location.lat === 0 && location.lng === 0);

  const isValid = hasValidLocation && selectedSpaceIds.length > 0;

  const handleNextRef = useRef<() => void>(() => {});
  handleNextRef.current = () => {
    if (!isValid) return;
    const selectedSpaces = allSpaces.filter((s) =>
      selectedSpaceIds.includes(s.id),
    );
    onNext(selectedSpaces);
  };

  useEffect(() => {
    registerNext(() => handleNextRef.current());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    onPreviewUpdate({ location, radiusKm, activeSpaceId, showOtherSpaces });
  }, [location, radiusKm, activeSpaceId, showOtherSpaces, onPreviewUpdate]);

  useEffect(() => {
    if (rankStatus === "loading") {
      setActiveSpaceId(null);
      onPreviewUpdate({ rankedSpaces: [], activeSpaceId: null });
    } else if (rankStatus === "success") {
      const spaces = [perfectMock, ...rankedSpaces];
      const firstId = spaces.length > 0 ? spaces[0].id : null;
      setActiveSpaceId(firstId);
      onPreviewUpdate({ rankedSpaces: spaces, activeSpaceId: firstId });
    }
  }, [rankStatus, rankedSpaces]); // eslint-disable-line react-hooks/exhaustive-deps

  const fakeDiagnosis = useCallback(
    (): DiagnosisData => ({
      dealId: "",
      companyName: "Demonstração Parceiro",
      contactName: "",
      contactEmail: "",
      segment: "",
      targetCity: location?.label ?? "",
      positionsMin: capacityMin !== "" ? Number(capacityMin) : "",
      positionsMax: capacityMax !== "" ? Number(capacityMax) : "",
      monthlyBudget: "",
      needs: "",
    }),
    [location, capacityMin, capacityMax],
  );

  const handleSearch = useCallback(() => {
    if (!hasValidLocation || !location) return;

    trackPartnerDemoSearchInitiated(
      {
        userId: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
      },
      {
        locationLabel: location.label,
        radiusKm,
        capacityMin: capacityMin !== "" ? Number(capacityMin) : null,
        capacityMax: capacityMax !== "" ? Number(capacityMax) : null,
      },
    );

    const body = buildSearchAndRankBody(fakeDiagnosis(), {
      city: location.label,
      latitude: location.lat,
      longitude: location.lng,
      radius_km: radiusKm,
      min_capacity: capacityMin !== "" ? Number(capacityMin) : null,
      max_capacity: capacityMax !== "" ? Number(capacityMax) : null,
      unit_credit_price: 80,
      preferred_neighborhoods: [],
    });
    lastBodyRef.current = body;
    searchStartTimeRef.current = Date.now();
    setSelectedSpaceIds([]);
    searchAndRank(body, { searchSource: effectiveSearchSource });
  }, [
    hasValidLocation,
    location,
    radiusKm,
    capacityMin,
    capacityMax,
    searchAndRank,
    effectiveSearchSource,
    fakeDiagnosis,
    userInfo,
  ]);

  const handleRetry = useCallback(() => {
    if (!lastBodyRef.current) return;
    searchStartTimeRef.current = Date.now();
    setSelectedSpaceIds([]);
    searchAndRank(lastBodyRef.current, { searchSource: effectiveSearchSource });
  }, [searchAndRank, effectiveSearchSource]);

  const handleLocationSelect = useCallback(
    async (spaceData?: { address: string; name: string; placeId: string }) => {
      if (!spaceData) {
        setLocation(null);
        setLocationInputValue("");
        return;
      }
      const label =
        `${spaceData.name}${spaceData.address ? ` - ${spaceData.address}` : ""}`.trim();
      let lat = -15.7942;
      let lng = -47.8822;
      try {
        const response = await spaceService.getSpaceByLocation(label);
        const result = Array.isArray(response) ? response[0] : response;
        if (result?.lat != null && result?.lng != null) {
          lat = Number(result.lat);
          lng = Number(result.lng);
        }
      } catch {
        // keep defaults
      }
      setLocation({ label, lat, lng });
      setLocationInputValue(label);
    },
    [],
  );

  const toggleSpace = useCallback(
    (id: string) => {
      setSelectedSpaceIds((prev) => {
        const next = prev.includes(id)
          ? prev.filter((s) => s !== id)
          : [...prev, id];
        trackPartnerDemoSpaceSelected(
          {
            userId: userInfo?.user?.id ?? null,
            email: userInfo?.profile?.email ?? null,
          },
          id,
          !prev.includes(id),
          next.length,
        );
        return next;
      });
    },
    [userInfo],
  );

  const capacityLabel = (() => {
    if (!capacityMin && !capacityMax) return null;
    if (capacityMin && capacityMax) return `${capacityMin}–${capacityMax}`;
    if (capacityMin) return `${capacityMin}+`;
    return `até ${capacityMax}`;
  })();

  return (
    <div className="flex flex-col h-full gap-2">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Localização</h2>
        {rankStatus === "idle" && (
          <p className="text-sm text-gray-500 mt-0.5">
            Busque a região do parceiro e selecione os espaços para o demo
          </p>
        )}
      </div>

      {/* Search bar */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setFiltersSheetOpen(true)}
            className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-left min-w-0 transition-colors hover:bg-[#F7F5F2]"
            style={{ border: "1px solid #E8E6E3", background: "white" }}
          >
            <MapPin
              className="w-3.5 h-3.5 shrink-0"
              style={{ color: location ? "#1A1814" : "#C8C4BE" }}
            />
            <span
              className="truncate"
              style={{ color: location ? "#1A1814" : "#9E9A94" }}
            >
              {location?.label ?? "Buscar localização..."}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setFiltersSheetOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium shrink-0 transition-colors hover:bg-[#ECEAE6]"
            style={{ background: "#F0EEEB", color: "#43403D" }}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filtros
          </button>

          <button
            type="button"
            onClick={handleSearch}
            disabled={rankStatus === "loading" || !hasValidLocation}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold shrink-0 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            style={{
              background: "#1A1814",
              color: "#FFD900",
              letterSpacing: "0.02em",
            }}
          >
            <Search className="w-3.5 h-3.5" />
            {rankStatus === "loading" ? "Buscando..." : "Buscar"}
          </button>
        </div>

        {/* Active filter chips */}
        {(radiusKm != null || capacityLabel) && (
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
            {radiusKm != null && (
              <button
                type="button"
                onClick={() => setFiltersSheetOpen(true)}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium shrink-0 transition-all"
                style={{ background: "#F0EEEB", color: "#43403D" }}
              >
                <CircleDashed className="w-3 h-3" />
                {radiusKm} km
              </button>
            )}
            {capacityLabel && (
              <button
                type="button"
                onClick={() => {
                  setCapacityMin("");
                  setCapacityMax("");
                }}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium shrink-0 transition-all"
                style={{ background: "#F0EEEB", color: "#43403D" }}
              >
                <Users className="w-3 h-3" />
                {capacityLabel}
                <X className="w-3 h-3 ml-0.5 opacity-50" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Filters sheet */}
      <Sheet open={filtersSheetOpen} onOpenChange={setFiltersSheetOpen}>
        <SheetContent
          side="right"
          className="px-0 pb-0 w-[520px] sm:max-w-[520px] flex flex-col"
        >
          <SheetHeader className="px-5 pb-3 pt-1">
            <SheetTitle className="text-base">Configurar busca</SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-5">
            {/* Location input */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <MapPin className="w-3 h-3" style={{ color: "#9E9A94" }} />
                <span
                  className="text-[10px] font-semibold uppercase"
                  style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                >
                  Localização
                </span>
              </div>
              <SearchAutocomplete
                ref={locationRef}
                options={searchSuggestions.map((s) => ({
                  value: s.placeId,
                  label: s.name || s.address,
                  description: s.address,
                  data: s,
                }))}
                value={locationInputValue}
                onValueChange={(_, spaceData) =>
                  handleLocationSelect(spaceData)
                }
                onInputChange={(value) => {
                  setLocationInputValue(value);
                  setLocation(null);
                  handleSearchSpaces(value);
                }}
                isLoading={isSearchLoading}
                placeholder="Ex: Itaim Bibi, São Paulo"
              />
            </div>

            {/* Radius */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <CircleDashed
                  className="w-3 h-3"
                  style={{ color: "#9E9A94" }}
                />
                <span
                  className="text-[10px] font-semibold uppercase"
                  style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                >
                  Raio
                </span>
              </div>
              <div className="flex items-center gap-2">
                {RADIUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setRadiusKm(opt.value);
                      setCustomRadiusMode(false);
                      setCustomRadiusInput("");
                    }}
                    className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
                    style={
                      !customRadiusMode && radiusKm === opt.value
                        ? {
                            background: "#1A1814",
                            color: "#FFD900",
                            border: "none",
                          }
                        : {
                            background: "#F0EEEB",
                            color: "#43403D",
                            border: "none",
                          }
                    }
                  >
                    {opt.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setCustomRadiusMode(true)}
                  className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
                  style={
                    customRadiusMode
                      ? {
                          background: "#1A1814",
                          color: "#FFD900",
                          border: "none",
                        }
                      : {
                          background: "#F0EEEB",
                          color: "#43403D",
                          border: "none",
                        }
                  }
                >
                  Outro
                </button>
              </div>
              {customRadiusMode && (
                <input
                  type="number"
                  placeholder="km"
                  value={customRadiusInput}
                  onChange={(e) => {
                    setCustomRadiusInput(e.target.value);
                    const n = Number(e.target.value);
                    if (n > 0) setRadiusKm(n);
                  }}
                  className="mt-2 w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-[#FFD900]"
                  style={{ borderColor: "#E8E6E3" }}
                />
              )}
            </div>

            {/* Capacity */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Users className="w-3 h-3" style={{ color: "#9E9A94" }} />
                <span
                  className="text-[10px] font-semibold uppercase"
                  style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                >
                  Capacidade
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Mínimo"
                  value={capacityMin}
                  onChange={(e) => setCapacityMin(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-[#FFD900]"
                  style={{ borderColor: "#E8E6E3" }}
                />
                <input
                  type="number"
                  placeholder="Máximo"
                  value={capacityMax}
                  onChange={(e) => setCapacityMax(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-[#FFD900]"
                  style={{ borderColor: "#E8E6E3" }}
                />
              </div>
            </div>
          </div>

          <div
            className="px-5 py-4 flex gap-2"
            style={{ borderTop: "1px solid #E8E6E3" }}
          >
            <SheetClose asChild>
              <button
                type="button"
                className="flex-1 py-2.5 rounded-full text-sm font-medium transition-all"
                style={{ background: "#F0EEEB", color: "#43403D" }}
              >
                Fechar
              </button>
            </SheetClose>
            <SheetClose asChild>
              <button
                type="button"
                onClick={handleSearch}
                disabled={rankStatus === "loading" || !hasValidLocation}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                style={{
                  background: "#1A1814",
                  color: "#FFD900",
                  letterSpacing: "0.03em",
                }}
              >
                <Search className="w-3.5 h-3.5" />
                Buscar
              </button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>

      {/* Results header */}
      {allSpaces.length > 0 && rankStatus !== "loading" && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: "#43403D" }}>
            Espaços disponíveis
          </span>
          <span className="text-xs" style={{ color: "#9E9A94" }}>
            {selectedSpaceIds.length} selecionado(s)
          </span>
        </div>
      )}

      {/* Space list */}
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {rankStatus === "loading" && <AISearchLoader />}
        {rankStatus === "loading" &&
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border p-3 animate-pulse"
              style={{ borderColor: "#E8E6E3", background: "white" }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="h-4 rounded flex-1"
                  style={{ background: "#F0EEEB" }}
                />
                <div
                  className="h-5 w-10 rounded-full"
                  style={{ background: "#F0EEEB" }}
                />
              </div>
              <div
                className="mt-1.5 h-3 w-2/3 rounded"
                style={{ background: "#F0EEEB" }}
              />
            </div>
          ))}
        {rankStatus === "error" && <AISearchError onRetry={handleRetry} />}

        {rankStatus !== "loading" &&
          rankStatus !== "error" &&
          (() => {
            const renderCard = (space: RankedSpace) => (
              <SpaceCard
                key={space.id}
                space={space}
                selected={selectedSpaceIds.includes(space.id)}
                active={activeSpaceId === space.id}
                onToggleSelect={() => toggleSpace(space.id)}
                onActivate={() =>
                  setActiveSpaceId(activeSpaceId === space.id ? null : space.id)
                }
                onViewDetails={() => setDrawerSpace(space)}
                onScoreClick={() => {}}
                hidePrice
              />
            );

            if (
              matchedSpaces.length === 0 &&
              otherSpaces.length === 0 &&
              rankStatus === "success"
            ) {
              return (
                <div
                  className="flex flex-col items-center text-center py-6 px-4 rounded-2xl"
                  style={{
                    background: "#F7F5F2",
                    border: "1px dashed #E0DDD9",
                  }}
                >
                  <Search
                    className="w-5 h-5 mb-2"
                    style={{ color: "#9E9A94" }}
                  />
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "#43403D" }}
                  >
                    Nenhum espaço encontrado
                  </p>
                  <p className="text-xs mt-1" style={{ color: "#9E9A94" }}>
                    Tente ajustar a localização ou os filtros
                  </p>
                </div>
              );
            }

            return (
              <>
                {matchedSpaces.map(renderCard)}
                {otherSpaces.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 py-1">
                      <div
                        className="flex-1 h-px"
                        style={{ background: "#E8E6E3" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowOtherSpaces((v) => !v)}
                        className="text-xs font-medium shrink-0"
                        style={{ color: "#78736D" }}
                      >
                        {showOtherSpaces
                          ? "Ocultar"
                          : `Ver mais ${otherSpaces.length} opções`}
                      </button>
                      <div
                        className="flex-1 h-px"
                        style={{ background: "#E8E6E3" }}
                      />
                    </div>
                    {showOtherSpaces && otherSpaces.map(renderCard)}
                  </>
                )}
              </>
            );
          })()}
      </div>

      {/* Space detail drawer */}
      {drawerSpace && (
        <SpaceDetailDrawer
          space={drawerSpace}
          open={!!drawerSpace}
          onClose={() => setDrawerSpace(null)}
          hidePrice
        />
      )}
    </div>
  );
}
